function nullableAmount(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function amountBounds(rule) {
  return {
    min: nullableAmount(rule.amount_min, 0),
    max: nullableAmount(rule.amount_max, Number.POSITIVE_INFINITY),
  };
}

function amountRangesCover(senderRule, approverRules) {
  const sender = amountBounds(senderRule);
  const ranges = approverRules
    .map(amountBounds)
    .filter((range) => range.max >= sender.min && range.min <= sender.max)
    .sort((left, right) => left.min - right.min || right.max - left.max);
  if (!ranges.length || ranges[0].min > sender.min) return false;

  let coveredThrough = sender.min;
  for (const range of ranges) {
    if (range.min > coveredThrough + 0.010001) return false;
    if (range.max > coveredThrough) coveredThrough = range.max;
    if (coveredThrough >= sender.max) return true;
  }

  return coveredThrough >= sender.max;
}

function scopeCovers(approverRule, senderRule, lookups) {
  if (!approverRule.family_id && !approverRule.subfamily_id && !approverRule.category_id) {
    return true;
  }

  if (!senderRule.family_id && !senderRule.subfamily_id && !senderRule.category_id) {
    return false;
  }

  if (approverRule.category_id) {
    return (
      Boolean(senderRule.category_id) &&
      Number(approverRule.category_id) === Number(senderRule.category_id)
    );
  }

  if (approverRule.subfamily_id) {
    if (senderRule.subfamily_id) {
      return Number(approverRule.subfamily_id) === Number(senderRule.subfamily_id);
    }
    if (senderRule.category_id) {
      const category = lookups.categoriesById.get(Number(senderRule.category_id));
      return Number(approverRule.subfamily_id) === Number(category?.subfamily_id);
    }
    return false;
  }

  if (senderRule.family_id) {
    return Number(approverRule.family_id) === Number(senderRule.family_id);
  }

  const senderSubfamilyId = senderRule.subfamily_id
    ? Number(senderRule.subfamily_id)
    : Number(lookups.categoriesById.get(Number(senderRule.category_id))?.subfamily_id);
  const senderSubfamily = lookups.subfamiliesById.get(senderSubfamilyId);

  return Number(approverRule.family_id) === Number(senderSubfamily?.family_id);
}

export function getCoveringApproverRules(senderRule, approverRules, lookups) {
  if (!senderRule || senderRule.action !== "requires_approval" || senderRule.is_active === false) {
    return [];
  }

  const sender = amountBounds(senderRule);
  const matchingScopeRules = (approverRules || []).filter((approverRule) => {
    if (approverRule.is_active === false) return false;
    if (approverRule.approver?.is_active === false) return false;
    if (lookups.usersById && !lookups.usersById.has(Number(approverRule.approver_user_id))) return false;
    const range = amountBounds(approverRule);
    if (range.max < sender.min || range.min > sender.max) return false;
    if (approverRule.requester_source && approverRule.requester_source !== (senderRule.requester_source || "manual")) return false;
    if (
      approverRule.requester_user_id &&
      Number(approverRule.requester_user_id) !== Number(senderRule.user_id)
    ) {
      return false;
    }

    return scopeCovers(approverRule, senderRule, lookups);
  });

  return amountRangesCover(senderRule, matchingScopeRules) ? matchingScopeRules : [];
}

export function hasApproverCoverage(senderRule, approverRules, lookups) {
  return getCoveringApproverRules(senderRule, approverRules, lookups).length > 0;
}
